import { useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { CreateSessionParams } from '~entities/session/api/create-session';
import { useGetSessionById } from '~entities/session/api/get-session-by-id';
import { useUpdateSessionByIdMutation } from '~entities/session/api/update-session-by-id';

import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';

import { SessionForm } from './session-form';

export const EditSessionPage = ({ sessionId }: { sessionId: string }) => {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = useNavigate();
  const { data, isFetching, refetch } = useGetSessionById(sessionId);

  // Track local form state for optimistic updates
  const [localFormData, setLocalFormData] =
    useState<CreateSessionParams | null>(null);

  const mutation = useUpdateSessionByIdMutation();
  const handleSubmit = async (value: CreateSessionParams) => {
    try {
      // Update local state immediately for optimistic UI
      setLocalFormData(value);

      // Submit to server
      await mutation.mutateAsync({ ...value, id: sessionId });

      // Show success message
      toast.success('Session updated successfully.');

      // Refetch data to ensure consistency
      await refetch();

      // Navigate after a small delay to ensure UI updates
      setTimeout(() => {
        void navigate({
          to: '/sessions',
        });
      }, 300);
    } catch (error) {
      console.error('Failed to update session:', error);
      toast.error('Failed to update session. Please try again.');
      // Reset local form data on error
      setLocalFormData(null);
    }
  };

  if (isFetching) {
    return (
      <div className='flex h-32 items-center justify-center'>
        <Loader2Icon className='size-8 animate-spin' />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Prepare form values - use local data if available (for optimistic UI updates)
  const formValues = {
    sessionName: localFormData?.name ?? data.name,
    startDate: localFormData?.startDate ?? data.startDate,
    endDate: localFormData?.endDate ?? data.endDate,
    isRegistrationFeeRequired:
      localFormData?.isRegistrationFee ?? data.isRegistrationFee,
    registrationFee:
      localFormData?.registrationFeeAmount ?? data.registrationFeeAmount,
    isClassDefault: localFormData?.isClassDefault ?? data.isClassDefault,
    isEventDefault: localFormData?.isEventDefault ?? data.isEventDefault,
    billingDate: localFormData?.billingDate ?? data.billingDate,
    registrationFeePerStudent:
      localFormData?.registrationFeePerStudent ??
      data.registrationFeePerStudent,
    visibleInParentPortal:
      localFormData?.visibleInParentPortal ?? data.visibleInParentPortal,
    chargeLastMonthAtCheckout:
      localFormData?.chargeLastMonthAtCheckout ??
      data.chargeLastMonthAtCheckout,
    chargeFirstMonthHourlyAtCheckout:
      localFormData?.chargeFirstMonthHourlyAtCheckout ??
      data.chargeFirstMonthHourlyAtCheckout,
    chargeSessionFeesAtEnrollment:
      localFormData?.chargeSessionFeesAtEnrollment ??
      data.chargeSessionFeesAtEnrollment,
  };

  return (
    <div>
      <div className='mb-8 flex items-center gap-2'>
        <Button
          variant={'ghost'}
          className='flex items-center p-2 [&_svg]:size-6'
          disabled={!canGoBack || mutation.isPending}
          onClick={() => {
            router.history.back();
          }}
        >
          <CircleArrowLeft className='size-6' />
        </Button>
        <h2 className='text-2xl font-semibold'>Edit Session</h2>
      </div>
      <Card className='mx-auto w-full max-w-4xl border border-border pt-4 shadow-none'>
        <CardContent>
          <SessionForm
            handleSubmit={handleSubmit}
            isPending={mutation.isPending}
            defaultValues={formValues}
            buttonContent='Update Session'
          />
        </CardContent>
      </Card>
    </div>
  );
};
